import { IconArrowDown } from "@/assets/icons/geist/IconArrowDown";
import { Button } from "@/components/flexkit/Button";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { CompactProjectCard } from "@/components/projects/cards/CompactProjectCard";
import { Flags } from "@/feature-flags";
import { useFlag } from "@/hooks/use-flag";
import { getRepoUrlFromInitializer } from "@/queries/environment-queries";
import { useListGroups, useListRoleAssignments } from "@/queries/group-queries";
import { useMembers } from "@/queries/organization-queries";
import { useListProjectsPaginated, useListProjectStates } from "@/queries/project-queries";
import {
    useListAllRunnerSCMIntegrations,
    useListRunnerEnvironmentClasses,
} from "@/queries/runner-configuration-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { useMemo, type FC } from "react";
import { ProjectCard } from "./cards/ProjectCard";

export const ProjectsListPaginated: FC<{
    canEdit?: boolean;
    showRecentlyUsedOnly?: boolean;
    search?: string;
    selectProjectId?: string;
    selectEnvironmentClass?: string;
    selectRunnerId?: string;
    useCompactLayout?: boolean;
    hasNextPage?: boolean;
    fetchNextPage?: () => void;
    isFetchingNextPage?: boolean;
    onCreateSuccess?: () => void;
}> = ({
    canEdit,
    search,
    selectProjectId,
    selectEnvironmentClass,
    selectRunnerId,
    useCompactLayout,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    onCreateSuccess,
}) => {
    const { data: user, isLoading: isLoadingUser } = useAuthenticatedUser();
    const { data, isPending: isPendingProjects } = useListProjectsPaginated({ search });

    const projectData = useMemo(() => {
        if (!data) return { projects: [] };
        return {
            projects: data.pages.flatMap((page) => page.projects),
        };
    }, [data]);

    const { data: projectsStates } = useListProjectStates(projectData.projects.map((p) => p.id));
    const { value: isGroupManagementEnabled } = useFlag(Flags.GroupManagementEnabled, false);
    const { data: groupsData } = useListGroups();

    // Fetch all role assignments for PROJECT resources once at the list level
    // This is more efficient than fetching per-project in each card
    // Omit resourceId to fetch all assignments for this resource type
    const { data: allProjectRoleAssignments } = useListRoleAssignments({
        resourceType: ResourceType.PROJECT,
        enabled: isGroupManagementEnabled,
    });

    const { data: membersData } = useMembers();

    const allProjects = useMemo(() => {
        if (!projectData) {
            return []; // should be caught by isLoading check
        }

        return (projectData.projects ?? []).map((project) => {
            return {
                project,
                state: projectsStates?.find((p) => p.projectId === project.id),
            };
        });
    }, [projectData, projectsStates]);

    const { data: runnerEnvironmentClasses } = useListRunnerEnvironmentClasses(selectRunnerId);
    const { data: scmIntegrations } = useListAllRunnerSCMIntegrations();

    const filteredByState = useMemo(() => {
        return allProjects.filter((p) => {
            if (selectProjectId) {
                return p.project.id === selectProjectId;
            }
            if (selectEnvironmentClass) {
                if (selectEnvironmentClass === "local") {
                    return !!p.state?.environmentClasses?.find(
                        (ec) => ec.environmentClass.case === "localRunner" && ec.environmentClass.value === true,
                    );
                } else {
                    return !!p.state?.environmentClasses?.find(
                        (ec) =>
                            ec.environmentClass.case === "environmentClassId" &&
                            ec.environmentClass.value === selectEnvironmentClass,
                    );
                }
            }
            if (selectRunnerId) {
                return runnerEnvironmentClasses?.some(
                    (rec) =>
                        !!p.state?.environmentClasses?.find(
                            (ec) =>
                                ec.environmentClass.case === "environmentClassId" &&
                                ec.environmentClass.value === rec.id,
                        ),
                );
            }

            return true;
        });
    }, [allProjects, selectEnvironmentClass, selectProjectId, selectRunnerId, runnerEnvironmentClasses]);

    const isLoading = isLoadingUser || !user || isPendingProjects || !projectData;

    if (isLoading) {
        return <LoadingState delay={true} />;
    }

    const noProjects = allProjects.length === 0;

    return (
        <div className="flex w-full flex-col gap-4 pb-6" data-testid="projects-list-paginated">
            <ul
                className={
                    useCompactLayout
                        ? "flex w-full flex-col gap-2"
                        : "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3"
                }
            >
                {filteredByState.map(({ project, state }) => {
                    const projectContextUrl = getRepoUrlFromInitializer(project.initializer);
                    const scmId =
                        projectContextUrl &&
                        scmIntegrations?.find((int) => int.host === new URL(projectContextUrl).host)?.scmId;

                    // Filter role assignments to only those for this specific project
                    const projectRoleAssignments = allProjectRoleAssignments?.assignments?.filter(
                        (a) => a.resourceId === project.id,
                    );

                    return (
                        <li key={project.id}>
                            {useCompactLayout ? (
                                <CompactProjectCard
                                    project={project}
                                    orgMembers={membersData?.members}
                                    forceHoverState={!!selectProjectId}
                                    onCreateSuccess={onCreateSuccess}
                                />
                            ) : (
                                <ProjectCard
                                    canEdit={useCompactLayout ? false : canEdit ?? false}
                                    project={project}
                                    state={state}
                                    orgMembers={membersData?.members}
                                    currentUserId={user?.id ?? ""}
                                    forceHoverState={!!selectProjectId}
                                    onCreateSuccess={onCreateSuccess}
                                    scmId={scmId}
                                    roleAssignments={projectRoleAssignments}
                                    allGroups={groupsData?.groups}
                                />
                            )}
                        </li>
                    );
                })}
            </ul>

            {noProjects &&
                (search?.trim() ? (
                    <EmptyStateCard
                        title="No results found"
                        description="No projects match your search criteria."
                        data-testid="no-search-results-empty-state"
                    />
                ) : (
                    <EmptyStateCard
                        title="No projects"
                        description="Projects are pre-configured templates that provide one-click environments. Only admins can create projects for your organization."
                        data-testid="no-projects-empty-state"
                    />
                ))}

            {/* Load more button (following Inventory.tsx:402-413 pattern) */}
            <div className="flex w-full flex-row justify-center py-8">
                {hasNextPage && (
                    <Button
                        variant="ghost"
                        title="Load more projects"
                        onClick={() => fetchNextPage?.()}
                        disabled={isFetchingNextPage}
                        data-tracking-id="load-more-projects"
                    >
                        <IconArrowDown size="lg" />
                        <span>{isFetchingNextPage ? "Loading..." : "Load more"}</span>
                    </Button>
                )}
            </div>
        </div>
    );
};
